import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  FileText,
  IndianRupee,
  AlertTriangle,
  Plus,
  Receipt,
  ArrowUpRight,
  Crown
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const businessId = localStorage.getItem('currentBusiness');

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get(`/api/reports/${businessId}/dashboard`);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    { title: 'Total Sales', value: `₹${(summary?.totalSales || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, icon: TrendingUp, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', testId: 'total-sales-card' },
    { title: 'Total Invoices', value: summary?.totalInvoices || '0', icon: FileText, color: 'bg-blue-50 text-blue-700 border-blue-200', testId: 'total-invoices-card' },
    { title: 'Tax Collected', value: `₹${(summary?.totalTaxCollected || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, icon: Receipt, color: 'bg-violet-50 text-violet-700 border-violet-200', testId: 'tax-collected-card' },
    { title: 'Receivables', value: `₹${(summary?.totalReceivable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, icon: IndianRupee, color: 'bg-amber-50 text-amber-700 border-amber-200', testId: 'receivables-card' },
    { title: 'Total Received', value: `₹${(summary?.totalReceived || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, icon: ArrowUpRight, color: 'bg-teal-50 text-teal-700 border-teal-200', testId: 'total-received-card' },
    { title: 'Customers', value: summary?.totalCustomers || '0', icon: Users, color: 'bg-sky-50 text-sky-700 border-sky-200', testId: 'total-customers-card' },
    { title: 'Low Stock', value: summary?.lowStockItems || '0', icon: AlertTriangle, color: 'bg-red-50 text-red-700 border-red-200', testId: 'low-stock-card' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading tracking-tight text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Welcome back! Here's your business overview</p>
        </div>
        <Button onClick={() => navigate('/invoices/create')} className="bg-orange-600 hover:bg-orange-700 shadow-md" data-testid="create-invoice-button">
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className={`border hover:shadow-md transition-shadow`} data-testid={stat.testId}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{stat.title}</span>
                  <div className={`p-1.5 rounded-lg ${stat.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-xl font-bold font-heading">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Invoices</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')} data-testid="view-all-invoices-btn">View All</Button>
          </CardHeader>
          <CardContent>
            {summary?.recentInvoices && summary.recentInvoices.length > 0 ? (
              <div className="space-y-2">
                {summary.recentInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => navigate(`/invoices/${invoice.id}`)} data-testid="recent-invoice-item">
                    <div>
                      <div className="font-medium text-sm">{invoice.invoiceNo}</div>
                      <div className="text-xs text-gray-500">{invoice.customer?.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-teal-700 text-sm">₹{invoice.total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                      <div className="text-xs text-gray-400">{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('en-IN') : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No invoices yet. Create your first invoice!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Customers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> Top Customers</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.topCustomers && summary.topCustomers.length > 0 ? (
              <div className="space-y-3">
                {summary.topCustomers.map((tc, i) => (
                  <div key={tc.customerId} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-4">{i + 1}.</span>
                      <span className="text-sm font-medium truncate max-w-[140px]">{tc.customerName}</span>
                    </div>
                    <span className="text-sm font-bold text-teal-700">₹{tc.totalAmount?.toLocaleString('en-IN', { minimumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Customers', icon: Users, path: '/customers', testId: 'manage-customers-button' },
          { label: 'Items', icon: AlertTriangle, path: '/items', testId: 'manage-items-button' },
          { label: 'Reports', icon: TrendingUp, path: '/reports', testId: 'view-reports-button' },
          { label: 'Settings', icon: Receipt, path: '/settings', testId: 'view-settings-button' },
        ].map((action) => {
          const Icon = action.icon;
          return (
            <Button key={action.label} variant="outline" className="h-20 flex-col gap-1.5" onClick={() => navigate(action.path)} data-testid={action.testId}>
              <Icon className="w-5 h-5" />
              <span className="text-xs">{action.label}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
